import { getAuth } from 'firebase/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

async function authHeaders() {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const headers = { 'Content-Type': 'application/json' };
  if (currentUser) {
    const token = await currentUser.getIdToken();
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

const EXTERNAL_EVENTS_TIMEOUT_MS = 8000;

async function request(path, options = {}) {
  const headers = options.skipAuth ? { 'Content-Type': 'application/json' } : await authHeaders();
  const { timeoutMs, signal: externalSignal, ...fetchOptions } = options;
  let signal = externalSignal;
  let timeoutId;
  if (timeoutMs && !signal) {
    const controller = new AbortController();
    signal = controller.signal;
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  }
  try {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      ...fetchOptions,
      headers: { ...headers, ...fetchOptions.headers },
      signal,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const isHtml = /^\s*<\s*!?\s*doctype|^\s*<\s*html\s/i.test(text);
      const isServerError = res.status >= 500;
      if (isHtml || isServerError) {
        throw new Error(isServerError ? 'Server error. Please try again later.' : 'Request failed. Please try again.');
      }
      try {
        const parsed = JSON.parse(text);
        const msg = parsed?.error ?? parsed?.detail ?? parsed?.message;
        if (msg && typeof msg === 'string') throw new Error(msg);
      } catch (e) {
        if (e instanceof Error && e.message && !e.message.startsWith('Unexpected')) throw e;
      }
      throw new Error(text || `Request failed (${res.status})`);
    }
    if (res.status === 204) return null;
    const contentType = res.headers.get('content-type') || '';
    const text = await res.text();
    if (!contentType.includes('application/json')) {
      const isHtml = /^\s*<\s*!?\s*doctype|^\s*<\s*html\s/i.test(text);
      throw new Error(
        isHtml
          ? 'API returned an unexpected HTML response. Check REACT_APP_API_BASE_URL and that the backend is running.'
          : 'API returned a non-JSON response.'
      );
    }
    try {
      return text ? JSON.parse(text) : null;
    } catch {
      throw new Error('API returned invalid JSON.');
    }
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

// ── List & Detail ──

/**
 * Fetch events list. Supports same query params as mobile:
 * organizer_id, event_type, location_search, use_current_location, city, state, after,
 * include_past, is_virtual, organization, my_interested, page, page_size, collapse_series,
 * plus sw_lat/sw_lng/ne_lat/ne_lng to restrict results to a map viewport.
 */
export async function fetchEvents(params = {}, options = {}) {
  const qs = new URLSearchParams();
  const supported = [
    'organizer_id', 'event_type', 'location_search', 'use_current_location', 'city', 'state',
    'after', 'include_past', 'is_virtual', 'organization', 'my_interested', 'my_rsvp_status',
    'page', 'page_size', 'collapse_series',
    'sw_lat', 'sw_lng', 'ne_lat', 'ne_lng', 'near_zip',
  ];
  supported.forEach((k) => {
    const v = params[k];
    if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
  });
  const data = await request(`/api/events/?${qs.toString()}`);
  const results = data && Array.isArray(data.results)
    ? data.results
    : (Array.isArray(data) ? data : []);
  if (options.returnMeta) {
    return { results, nearZipAnchor: data?.near_zip_anchor || null };
  }
  return results;
}

/**
 * Fetch external (Mobilize) events via live proxy. Nothing is stored in CivixThread DB.
 * Supports same location/type filters as native list where possible, plus a
 * map viewport via sw_lat/sw_lng/ne_lat/ne_lng.
 *
 * Resolves to { results, partial }. `partial` is true when the proxy could not
 * reach every Mobilize organization (or the request failed outright), so callers
 * can offer a retry rather than reporting an empty area as empty.
 */
export async function fetchExternalEvents(params = {}) {
  const qs = new URLSearchParams();
  const supported = [
    'event_type', 'location_search', 'city', 'state', 'is_virtual',
    'sw_lat', 'sw_lng', 'ne_lat', 'ne_lng', 'near_zip',
  ];
  supported.forEach((k) => {
    const v = params[k];
    if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
  });
  try {
    const data = await request(`/api/events/external/?${qs.toString()}`, {
      skipAuth: true,
      timeoutMs: EXTERNAL_EVENTS_TIMEOUT_MS,
    });
    if (data && Array.isArray(data.results)) {
      return { results: data.results, partial: Boolean(data.partial) };
    }
    if (Array.isArray(data)) return { results: data, partial: false };
    return { results: [], partial: true };
  } catch (err) {
    console.warn('fetchExternalEvents failed:', err);
    return { results: [], partial: true };
  }
}

/** Fetch events organized by the given user (dashboard "my events"). */
export async function fetchUserEvents(userId, options = {}) {
  if (!userId) return [];
  return fetchEvents({
    organizer_id: userId,
    include_past: options.includePast ? 'true' : undefined,
  });
}

export async function fetchEventById(eventId) {
  return request(`/api/events/${eventId}/`);
}

export async function fetchEventTypes() {
  return request('/api/event-types/');
}

export async function fetchEventTypesForUser() {
  return request('/api/event-types/for-user/');
}

// ── Create / Update / Delete ──

export async function createEvent(eventData) {
  return request('/api/events/', {
    method: 'POST',
    body: JSON.stringify(eventData),
  });
}

export async function createEventSeries(seriesData) {
  return request('/api/events/series/', {
    method: 'POST',
    body: JSON.stringify(seriesData),
  });
}

export async function updateEvent(eventId, eventData, editScope = 'this_only') {
  return request(`/api/events/${eventId}/manage/?edit_scope=${editScope}`, {
    method: 'PUT',
    body: JSON.stringify(eventData),
  });
}

export async function deleteEvent(eventId, editScope = 'this_only') {
  return request(`/api/events/${eventId}/manage/?edit_scope=${editScope}`, {
    method: 'DELETE',
  });
}

// ── RSVP ──

export async function rsvpToEvent(eventId, status = 'interested') {
  if (status === null) {
    return request(`/api/events/${eventId}/rsvp/`, { method: 'DELETE' });
  }
  return request(`/api/events/${eventId}/rsvp/`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

export async function rsvpToEventSeries(seriesId, status = 'going') {
  if (status === null) {
    return request(`/api/events/series/${seriesId}/rsvp/`, { method: 'DELETE' });
  }
  return request(`/api/events/series/${seriesId}/rsvp/`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

export async function fetchEventRsvps(eventId, status) {
  const params = status ? `?status=${encodeURIComponent(status)}` : '';
  return request(`/api/events/${eventId}/rsvps/${params}`);
}

// ── Volunteer ──

export async function volunteerForEvent(eventId) {
  return request(`/api/events/${eventId}/volunteer/`, { method: 'POST' });
}

export async function fetchEventVolunteers(eventId) {
  return request(`/api/events/${eventId}/volunteers/`);
}

export async function updateVolunteerStatus(eventId, userId, status, notes = '') {
  return request(`/api/events/${eventId}/volunteers/${userId}/`, {
    method: 'PUT',
    body: JSON.stringify({ status, notes }),
  });
}

// ── Endorse ──

/** Account types allowed to endorse events (must match backend EVENT_ENDORSEMENT_ALLOWED_ACCOUNT_TYPES). */
export const ENDORSEMENT_ALLOWED_ACCOUNT_TYPES = [
  'organization',
  'non_profit',
  'municipality',
  'public_official_candidate',
  'state_politician',
  'federal_politician',
];

/** Normalize endorse API payload to an array of endorser objects. */
export function normalizeEventEndorsements(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

export async function fetchEventEndorsements(eventId) {
  const data = await request(`/api/events/${eventId}/endorse/`, { skipAuth: true });
  return normalizeEventEndorsements(data);
}

export async function endorseEvent(eventId, endorse = true) {
  return request(`/api/events/${eventId}/endorse/`, {
    method: endorse ? 'POST' : 'DELETE',
  });
}

// ── Follow-up Steps ──

export async function optInFollowups(eventId) {
  return request(`/api/events/${eventId}/opt-in-followups/`, { method: 'POST' });
}

export async function fetchPendingFollowups() {
  return request('/api/users/me/pending-followups/');
}

export async function completeFollowupStep(stepId) {
  return request(`/api/followup-steps/${stepId}/complete/`, { method: 'POST' });
}

export async function ignoreFollowupStep(stepId) {
  return request(`/api/followup-steps/${stepId}/ignore/`, { method: 'POST' });
}

// ── QR Codes & Check-in ──

export async function getEventQRCodes(eventId) {
  return request(`/api/events/${eventId}/qr-codes/`);
}

export async function checkInToEvent(eventId, token) {
  return request(`/api/events/${eventId}/check-in/`, {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function checkInAsVolunteer(eventId, token) {
  return request(`/api/events/${eventId}/volunteer-check-in/`, {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

// ── Image Upload ──
// Backend expects JSON: { image_data: base64String, content_type: "image/jpeg" }

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export async function uploadEventImage(file) {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const dataUrl = await readFileAsDataUrl(file);
  const contentType = file.type || 'image/jpeg';
  let imageData = dataUrl;
  if (dataUrl.startsWith('data:')) {
    const base64Part = dataUrl.split(',')[1];
    if (base64Part) imageData = base64Part;
  }
  const headers = {
    'Content-Type': 'application/json',
  };
  if (currentUser) {
    const token = await currentUser.getIdToken();
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE_URL}/api/events/upload-image/`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ image_data: imageData, content_type: contentType }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = data?.error || res.statusText || 'Image upload failed';
    throw new Error(message);
  }
  return data;
}
